/**
 * Microphone capture for the Connie voice socket: mono, 16 kHz, 16-bit
 * little-endian PCM, about 100 ms per chunk.
 *
 * The AudioContext runs at the device rate (Firefox refuses to connect a mic
 * stream to a context at another rate), and the worklet resamples to 16 kHz.
 */

export const PCM_SAMPLE_RATE = 16000;
const CHUNK_SAMPLES = PCM_SAMPLE_RATE / 10;

// Inline so it needs no separate asset in the build.
const WORKLET_SOURCE = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    this.ratio = sampleRate / ${PCM_SAMPLE_RATE};
    this.chunk = new Int16Array(${CHUNK_SAMPLES});
    this.filled = 0;
    this.position = 0;
    this.sumSquares = 0;
  }

  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (!channel) return true;

    // Linear-interpolated resample from the device rate to 16 kHz.
    while (this.position < channel.length - 1) {
      const index = Math.floor(this.position);
      const fraction = this.position - index;
      const sample = channel[index] + (channel[index + 1] - channel[index]) * fraction;
      const clamped = Math.max(-1, Math.min(1, sample));
      this.chunk[this.filled++] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
      this.sumSquares += clamped * clamped;
      this.position += this.ratio;

      if (this.filled === this.chunk.length) {
        const level = Math.sqrt(this.sumSquares / this.chunk.length);
        const buffer = this.chunk.buffer.slice(0);
        this.port.postMessage({ buffer, level }, [buffer]);
        this.filled = 0;
        this.sumSquares = 0;
      }
    }
    this.position -= channel.length - 1;
    return true;
  }
}
registerProcessor('pcm-capture', PcmCaptureProcessor);
`;

export type PcmCapture = {
  stop: () => void;
};

export class MicrophoneUnavailableError extends Error {
  constructor(
    message: string,
    readonly denied: boolean
  ) {
    super(message);
    this.name = 'MicrophoneUnavailableError';
  }
}

/**
 * Starts the microphone. `onChunk` gets each ~100 ms PCM buffer (Int16 LE, as
 * an ArrayBuffer ready to send) and its RMS level, 0–1.
 */
export const startPcmCapture = async (
  onChunk: (pcm: ArrayBuffer, level: number) => void
): Promise<PcmCapture> => {
  if (
    typeof navigator === 'undefined' ||
    !navigator.mediaDevices?.getUserMedia ||
    typeof AudioWorkletNode === 'undefined'
  ) {
    throw new MicrophoneUnavailableError(
      'Talking to Connie is not supported in this browser.',
      false
    );
  }

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
  } catch (error) {
    const denied =
      error instanceof DOMException &&
      (error.name === 'NotAllowedError' || error.name === 'SecurityError');
    throw new MicrophoneUnavailableError(
      denied
        ? 'Allow microphone access to talk to Connie.'
        : 'No microphone was found.',
      denied
    );
  }

  const context = new AudioContext();
  const stopAll = () => {
    stream.getTracks().forEach(track => track.stop());
    void context.close().catch(() => undefined);
  };

  try {
    const moduleUrl = URL.createObjectURL(
      new Blob([WORKLET_SOURCE], { type: 'application/javascript' })
    );
    try {
      await context.audioWorklet.addModule(moduleUrl);
    } finally {
      URL.revokeObjectURL(moduleUrl);
    }

    const source = context.createMediaStreamSource(stream);
    const node = new AudioWorkletNode(context, 'pcm-capture', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      channelCountMode: 'explicit',
    });
    node.port.onmessage = (event: MessageEvent) => {
      const { buffer, level } = event.data as {
        buffer: ArrayBuffer;
        level: number;
      };
      onChunk(buffer, level);
    };
    // A silent sink keeps the graph pulling the worklet in every browser.
    const sink = context.createGain();
    sink.gain.value = 0;
    source.connect(node);
    node.connect(sink);
    sink.connect(context.destination);
    if (context.state === 'suspended') await context.resume();

    return {
      stop: () => {
        node.port.onmessage = null;
        source.disconnect();
        node.disconnect();
        sink.disconnect();
        stopAll();
      },
    };
  } catch (error) {
    stopAll();
    throw error;
  }
};
